import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Form, Select, Button, Space, Typography, message,
  Table, Alert, Tag, DatePicker, Empty, InputNumber, Divider
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, InfoCircleOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import logger from '../../services/logger';
import { safeFormatTimestamp } from '../../utils/timezone';

const { Option } = Select;
const { Text, Title } = Typography;

const CreateInvoice = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [uninvoicedRentals, setUninvoicedRentals] = useState([]);
  const [selectedRentalIds, setSelectedRentalIds] = useState([]);
  const [invoiceDate, setInvoiceDate] = useState(dayjs());
  const [cutoffDate, setCutoffDate] = useState(dayjs());
  const [vatRate, setVatRate] = useState(0);
  const [loadingRentals, setLoadingRentals] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/customers');
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error('Müşteriler yüklenirken hata oluştu');
    }
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const fetchUninvoicedRentals = useCallback(async () => {
    if (!selectedCustomerId) { setUninvoicedRentals([]); return; }
    setLoadingRentals(true);
    try {
      const response = await axios.get('/api/v1/billing/uninvoiced-rentals', {
        params: {
          customer_id: selectedCustomerId,
          // Seçilen bitiş tarihinin gün sonuna kadar olan seansları dahil et
          cutoff_date: cutoffDate ? cutoffDate.endOf('day').toISOString() : undefined,
        }
      });
      setUninvoicedRentals(Array.isArray(response.data) ? response.data : []);
      setSelectedRentalIds([]);
      setShowPreview(false);
    } catch (error) {
      message.error('Kiralama verileri yüklenirken hata oluştu');
      setUninvoicedRentals([]);
    } finally {
      setLoadingRentals(false);
    }
  }, [selectedCustomerId, cutoffDate]);

  useEffect(() => { fetchUninvoicedRentals(); }, [fetchUninvoicedRentals]);

  const handleCreateInvoice = async () => {
    if (!selectedCustomerId || selectedRentalIds.length === 0) {
      message.warning('Müşteri seçin ve en az bir kiralama işaretleyin');
      return;
    }
    setCreating(true);
    try {
      const cutoffParam = cutoffDate ? `&cutoff_date=${encodeURIComponent(cutoffDate.endOf('day').toISOString())}` : '';
      const response = await axios.post(
        `/api/v1/billing/create-from-selected?customer_id=${selectedCustomerId}&rental_ids=${selectedRentalIds.join(',')}&invoice_date=${invoiceDate.toISOString()}&vat_rate=${vatRate}${cutoffParam}`
      );
      message.success(`Fatura oluşturuldu: ${response.data.invoice_number}`);
      navigate(`/billing/${response.data.invoice_id}`);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Fatura oluşturulurken hata oluştu');
    } finally {
      setCreating(false);
    }
  };

  const selectedRentals = uninvoicedRentals.filter(r => selectedRentalIds.includes(r.rental_id));
  const subtotal = selectedRentals.reduce((sum, r) => sum + (r.estimated_amount || 0), 0);
  const vatAmount = subtotal * (vatRate / 100);
  const totalAmount = subtotal + vatAmount;
  const customerName = customers.find(c => c.id === selectedCustomerId)?.name || '';

  const rentalColumns = [
    {
      title: 'Cihaz', dataIndex: 'device_name', key: 'device_name', width: 150,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Tip', dataIndex: 'billing_type', key: 'billing_type', width: 120,
      render: (type) => <Tag color={type === 'fixed_monthly' ? 'blue' : 'purple'}>{type === 'fixed_monthly' ? 'Sabit Aylık' : 'Atış Bazlı'}</Tag>,
    },
    {
      title: 'Başlangıç', dataIndex: 'start_date', key: 'start_date', width: 140,
      render: (d) => d ? safeFormatTimestamp(d) : '-',
    },
    {
      title: 'Bitiş', dataIndex: 'end_date', key: 'end_date', width: 140,
      render: (d) => d ? safeFormatTimestamp(d) : <Tag color="green">Aktif</Tag>,
    },
    {
      title: 'Faturalanan Dönem', key: 'billing_period', width: 200,
      render: (_, r) => {
        if (!r.billing_period_start || !r.billing_period_end) return '-';
        return (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 12 }}>
              {safeFormatTimestamp(r.billing_period_start)} → {safeFormatTimestamp(r.billing_period_end)}
            </Text>
            {r.previously_invoiced && (
              <Tag color="gold" style={{ fontSize: 10, marginTop: 2 }}>Kaldığı yerden</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Detay', key: 'detail', width: 200,
      render: (_, r) => {
        if (r.billing_type === 'fixed_monthly') return r.monthly_rate ? `₺${r.monthly_rate.toFixed(2)}/ay` : '-';
        if (r.billing_type === 'per_shot') return (
          <Space direction="vertical" size={0}>
            <Text>{r.shots_count || 0} atış x ₺{(r.unit_price || 0).toFixed(4)}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>{r.sessions_count || 0} seans</Text>
          </Space>
        );
        return '-';
      },
    },
    {
      title: 'Tutar', dataIndex: 'estimated_amount', key: 'estimated_amount', width: 120, align: 'right',
      render: (v) => <Text strong>₺{(v || 0).toFixed(2)}</Text>,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/billing')}>Geri</Button>
          <Title level={2} style={{ margin: 0 }}>
            <PlusOutlined /> Fatura Oluştur
          </Title>
        </Space>
      </div>

      <Alert
        message="Kiralama Bazlı Faturalandırma"
        description="Müşteri seçtiğinizde faturası kesilmemiş kiralamalar listelenir. İstediğiniz kiralamaları seçip KDV oranını belirleyerek fatura oluşturabilirsiniz."
        type="info" showIcon style={{ marginBottom: 16 }} icon={<InfoCircleOutlined />}
      />

      {/* Müşteri, Fatura Tarihi, KDV */}
      <Card title="Fatura Bilgileri" size="small" style={{ marginBottom: 16 }}>
        <Space size="large" wrap>
          <Form.Item label="Müşteri" style={{ marginBottom: 0 }}>
            <Select
              placeholder="Müşteri seçin"
              showSearch optionFilterProp="children"
              value={selectedCustomerId}
              onChange={(v) => { setSelectedCustomerId(v); setSelectedRentalIds([]); setShowPreview(false); }}
              style={{ width: 280 }}
            >
              {customers.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item label="Fatura Tarihi" style={{ marginBottom: 0 }}>
            <DatePicker value={invoiceDate} onChange={(d) => setInvoiceDate(d || dayjs())} format="DD.MM.YYYY" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item
            label="Seanslar Bitiş Tarihi"
            style={{ marginBottom: 0 }}
            tooltip="Kiralama başlangıcından (veya önceki faturanın bitişinden) bu tarihe kadar olan faturalanmamış seanslar hesaplanır. Sonraki seanslar bir sonraki faturaya kalır."
          >
            <DatePicker value={cutoffDate} onChange={(d) => setCutoffDate(d || dayjs())} format="DD.MM.YYYY" allowClear={false} style={{ width: 150 }} />
          </Form.Item>
          <Form.Item label="KDV Oranı (%)" style={{ marginBottom: 0 }}>
            <InputNumber min={0} max={50} value={vatRate} onChange={(v) => setVatRate(v || 0)} style={{ width: 100 }} />
          </Form.Item>
        </Space>
      </Card>

      {/* Faturalanmamış Kiralamalar */}
      {selectedCustomerId && (
        <Card title={`Faturalanmamış Kiralamalar (${uninvoicedRentals.length})`} size="small" style={{ marginBottom: 16 }}>
          {uninvoicedRentals.length > 0 ? (
            <>
              <Table
                columns={rentalColumns}
                dataSource={uninvoicedRentals}
                rowKey="rental_id"
                loading={loadingRentals}
                pagination={false}
                size="small"
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: selectedRentalIds,
                  onChange: (keys) => { setSelectedRentalIds(keys); setShowPreview(false); },
                }}
              />
              {selectedRentalIds.length > 0 && !showPreview && (
                <div style={{ textAlign: 'right', marginTop: 12 }}>
                  <Button type="primary" icon={<EyeOutlined />} onClick={() => setShowPreview(true)}>
                    Önizleme ({selectedRentalIds.length} kiralama)
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Empty description={loadingRentals ? 'Yükleniyor...' : 'Bu müşterinin faturalanmamış kiralaması yok'} />
          )}
        </Card>
      )}

      {/* Fatura Önizleme */}
      {showPreview && selectedRentalIds.length > 0 && (
        <Card title="Fatura Önizleme" size="small" style={{ border: '2px solid #1890ff' }}>
          {/* Fatura başlık bilgileri */}
          <div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 6 }}>
            <Space size="large" wrap>
              <span><Text type="secondary">Müşteri:</Text> <Text strong>{customerName}</Text></span>
              <span><Text type="secondary">Fatura Tarihi:</Text> <Text strong>{invoiceDate.format('DD.MM.YYYY')}</Text></span>
              <span><Text type="secondary">Seanslar Bitiş:</Text> <Text strong>{cutoffDate.format('DD.MM.YYYY')}</Text></span>
              <span><Text type="secondary">KDV:</Text> <Text strong>%{vatRate}</Text></span>
            </Space>
          </div>

          {/* Seçili kalemler tablosu */}
          <Table
            columns={rentalColumns}
            dataSource={selectedRentals}
            rowKey="rental_id"
            pagination={false}
            size="small"
          />

          <Divider style={{ margin: '12px 0' }} />

          {/* Tutar özeti */}
          <div style={{ textAlign: 'right' }}>
            <Space direction="vertical" size={4} style={{ minWidth: 250 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Ara Toplam:</Text>
                <Text strong>₺{subtotal.toFixed(2)}</Text>
              </div>
              {vatRate > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>KDV (%{vatRate}):</Text>
                  <Text strong>₺{vatAmount.toFixed(2)}</Text>
                </div>
              )}
              <Divider style={{ margin: '4px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', background: '#f6ffed', padding: '8px 12px', borderRadius: 6, border: '1px solid #b7eb8f' }}>
                <Text strong style={{ fontSize: 16 }}>Genel Toplam:</Text>
                <Text strong style={{ fontSize: 20, color: '#3f8600' }}>₺{totalAmount.toFixed(2)}</Text>
              </div>
            </Space>
          </div>

          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={() => setShowPreview(false)}>Düzenle</Button>
              <Button type="primary" size="large" icon={<SaveOutlined />} onClick={handleCreateInvoice} loading={creating}>
                Fatura Oluştur
              </Button>
            </Space>
          </div>
        </Card>
      )}
    </div>
  );
};

export default CreateInvoice;
